import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';
import './ClassScheduleTab.css';

// Accent colors for schedule blocks
const accentColors = [
  { bg: "#eaf2fc", border: "#3984ec", time: "#3984ec" },
  { bg: "#e4fff5", border: "#34bc7c", time: "#34bc7c" },
];

function getAccent(i) {
  return accentColors[i % accentColors.length];
}

function timeRange(startTime, durationMins) {
  const [h, m] = startTime.split(":").map(Number);
  const start = new Date(2000, 1, 1, h, m);
  const end = new Date(start.getTime() + durationMins * 60000);
  const fmt = (d) =>
    d
      .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })
      .replace(/^24:/, "00:");
  return fmt(start) + "–" + fmt(end);
}

const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const dayAbbreviations = ["M", "T", "W", "T", "F", "S", "S"];

const ClassScheduleTab = ({ userId, classId }) => {
  const { axios, token } = useAuth();
  const [classSchedule, setClassSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedDay, setSelectedDay] = useState(() =>
    new Date().toLocaleString("en-US", { weekday: "long" })
  );


  useEffect(() => {
    if (!classId) {
      setError("No class ID provided");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${configData.CLASS_BY_ID}/${classId}`, {
          headers: { Accept: "application/json", Authorization: token },
        });

        const data = res.data;
        setClassSchedule(data.classScheduleMap || {});
        setError(null);
      } catch (e) {
        console.error("Error fetching class schedule:", e);
        setError("Failed fetching class schedule");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [classId, axios, token]);

  // Get schedule for the selected day
  const getScheduleForDay = () => {
    if (!classSchedule || !classSchedule[selectedDay]) return [];
    return classSchedule[selectedDay].sort((a, b) =>
      a.startTime.localeCompare(b.startTime)
    );
  };

  if (loading) return <Typography className="schedule-loading-fdb">Loading schedule...</Typography>;
  if (error) return <Typography className="schedule-error-fdb">{error}</Typography>;

  return (
    <Box className="tsc-card">
      <Typography variant="h6" className="schedule-title-fdb">
        Class Schedule
      </Typography>

      <Box className="day-selector-fdb">
        {dayOrder.map((day, index) => (
          <Box
            key={day}
            className={`day-button-fdb ${selectedDay === day ? "day-button-selected-fdb" : ""}`}
            onClick={() => setSelectedDay(day)}
          >
            {dayAbbreviations[index]}
          </Box>
        ))}
      </Box>

      {!getScheduleForDay().length ? (
        <Typography className="no-schedule-fdb">
          No scheduled classes found for {selectedDay}.
        </Typography>
      ) : (
        <Box className="schedule-list-fdb">
          {getScheduleForDay().map((s, idx) => {
            const accent = getAccent(idx);
            return (
              <Box
                key={idx}
                className="schedule-card-fdb"
                style={{
                  background: accent.bg,
                  borderLeft: `8px solid ${accent.border}`,
                }}
              >
                <Box className="schedule-info-fdb">
                  <Typography className="course-name-fdb" style={{ color: accent.border }}>
                    {s.courseName}
                  </Typography>
                  <Typography className="course-details-fdb" style={{ color: accent.border }}>
                    {s.courseCode} — {s.location || "Online"}
                  </Typography>
                  {s.topic && (
                    <Typography className="topic-text-fdb">Topic: {s.topic}</Typography>
                  )}
                 
                </Box>
                <Box className="time-box-fdb" style={{ backgroundColor: accent.time }}>
                  {timeRange(s.startTime, parseInt(s.durationMins, 10))}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ClassScheduleTab;
